import React from 'react';
import { Link } from 'react-router-dom';
import { Table, Button, Modal, Form, Input, Select, notification, Typography, Space, Tag } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { useCompanyId, CompanySelector } from '../../lib/books';

import { label } from '../../lib/format';
const CLIENT_TYPES = ['COMPANY', 'INDIVIDUAL', 'PARTNERSHIP'];

export default function ClientsPage() {
  const auth = useAuth();
  const { companyId } = useCompanyId();
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [form] = Form.useForm();

  const fetchClients = () => {
    if (!companyId) return;
    setLoading(true);
    request
      .get('/clients', { company_id: companyId })
      .then((r) => r.json())
      .then((r) => r.status && setData(r.data))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(fetchClients, [companyId]);

  const handleCreate = () => {
    form.validateFields().then((values) => {
      request
        .post('/clients', { ...values, company_id: companyId })
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: 'Client created' });
            setModalOpen(false);
            fetchClients();
          } else {
            notification.error({ message: r.message });
          }
        });
    });
  };

  const columns = [
    {
      title: 'Client Name',
      dataIndex: 'client_name',
      render: (v, record) => <Link to={`/clients/${record.id}`}>{v}</Link>,
    },
    { title: 'Type', dataIndex: 'client_type', render: (v) => label(v) },
    { title: 'Risk', dataIndex: 'risk_level', render: (v) => (v ? <Tag>{label(v)}</Tag> : '-') },
    { title: 'Status', dataIndex: 'status', render: (v) => <Tag color={v === 'ACTIVE' ? 'green' : 'default'}>{label(v)}</Tag> },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Clients
        </Typography.Title>
        <Space>
          <Input.Search placeholder="Search clients" allowClear style={{ width: 220 }} onSearch={setSearch} onChange={(e) => !e.target.value && setSearch('')} />
          <CompanySelector />
          {!auth.user.role?.includes('CLIENT_ADMIN') && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={!companyId}
              onClick={() => {
                form.resetFields();
                setModalOpen(true);
              }}
            >
              New Client
            </Button>
          )}
        </Space>
      </Space>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={search ? data.filter((c) => [c.client_name, c.client_code, c.registration_number, c.tax_number].some((x) => x && String(x).toLowerCase().includes(search.toLowerCase()))) : data}
        loading={loading}
        pagination={{ showTotal: (total) => `${total} in total`, showSizeChanger: true }}
        locale={{ emptyText: search ? 'No clients match your search' : 'No clients yet - add the first one with "New Client"' }}
      />

      <Modal title="New Client" open={modalOpen} onCancel={() => setModalOpen(false)} onOk={handleCreate}>
        <Form form={form} layout="vertical">
          <Form.Item name="client_name" label="Client Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="client_type" label="Type" initialValue="COMPANY">
            <Select options={CLIENT_TYPES.map((t) => ({ value: t, label: t }))} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
